import { applicantTiers } from '../data/applicantTiers';
import type { Applicant, ApplicantScoreFactor, ApplicantStatus, ApplicantTier, ApplicantTierId } from '../types/applicant';

const demandPoints = { High: 10, Medium: 6, Low: 2 } as const;

export function applicantBreakdown(a: Applicant): ApplicantScoreFactor[] {
  return [
    { label: 'Qualifications', points: a.qualificationScore, max: 30, detail: a.qualification },
    { label: 'Experience', points: Math.min(a.yearsExperience / 20, 1) * 25, max: 25, detail: `${a.yearsExperience} years` },
    { label: 'Social influence', points: (a.socialScore / 100) * 20, max: 20, detail: `${a.socialScore} / 100 AI social score` },
    { label: 'References', points: (a.referencesVerified / 3) * 15, max: 15, detail: `${a.referencesVerified} of ${a.referencesTotal} verified` },
    { label: 'Field demand', points: demandPoints[a.fieldDemand], max: 10, detail: `${a.fieldDemand} demand at BAK` },
  ];
}

export function applicantScore(a: Applicant): number {
  return Math.round(applicantBreakdown(a).reduce((s, f) => s + f.points, 0));
}

export function getApplicantTier(a: Applicant): ApplicantTier {
  const score = applicantScore(a);
  return [...applicantTiers].reverse().find((t) => score >= t.minScore) ?? applicantTiers[0];
}

export function isOpen(status: ApplicantStatus): boolean {
  return status !== 'Approved' && status !== 'Rejected';
}

export const applicantTierStyles: Record<ApplicantTierId, { badge: string; solid: string }> = {
  emerging: { badge: 'border-stone-200 bg-stone-50 text-stone-600', solid: 'bg-stone-200 text-stone-700' },
  promising: { badge: 'border-sky-200 bg-sky-50 text-sky-700', solid: 'bg-sky-100 text-sky-700' },
  strong: { badge: 'border-indigo-200 bg-indigo-50 text-indigo-700', solid: 'bg-indigo-100 text-indigo-700' },
  'fast-track': { badge: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700', solid: 'bg-fuchsia-100 text-fuchsia-700' },
};

export const applicantStatusStyles: Record<ApplicantStatus, string> = {
  New: 'border-sky-200 bg-sky-50 text-sky-700',
  'Under review': 'border-slate-200 bg-slate-50 text-slate-700',
  Interview: 'border-violet-200 bg-violet-50 text-violet-700',
  'Info requested': 'border-amber-200 bg-amber-50 text-amber-700',
  Approved: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Rejected: 'border-red-200 bg-red-50 text-red-700',
};
